import { useAuth } from '../context/AuthProvider';

export const usePermission = (requiredRoles: string[]): boolean => {
  const { profile } = useAuth();
  const hasPermission = requiredRoles.some((role: any) => profile?.userPermission?.rols?.includes(role));
  return hasPermission;
};

/**
 * Checks if a user can edit an intervention file based on ownership and role hierarchy.
 *
 * Access Control Rules:
 * 1. Admin and Manager roles can edit ANY intervention file
 * 2. Users can edit files where they are assigned as engineers (helionEngineers)
 * 3. Senior Technicians can edit files assigned to regular technicians
 * 4. Dispatchers can edit files assigned to technicians
 * 5. All other users cannot edit intervention files
 *
 * @param interventionFile - The intervention file to check permissions for
 * @param currentUser - The current user's profile object
 * @returns boolean - True if user can edit the file, false otherwise
 */
export const canEditInterventionFile = (interventionFile: any, currentUser: any): boolean => {
  if (!interventionFile || !currentUser) return false;

  const userRoles = currentUser?.userPermission?.rols || [];

  // Admin and manager can edit any file
  if (userRoles.includes('admin') || userRoles.includes('manager')) {
    return true;
  }

  // Check if the current user is one of the assigned engineers
  const isAssignedEngineer = interventionFile?.helionEngineers?.some(
    (engineer: any) => engineer?.userId === currentUser?.id || engineer?.user?.id === currentUser?.id
  );

  if (isAssignedEngineer) {
    return true;
  }

  // Senior technicians can edit files assigned to technicians
  if (userRoles.includes('senior_tehnician')) {
    const hasTechnicianAssigned = interventionFile?.helionEngineers?.some((engineer: any) => {
      const engineerRoles = engineer?.user?.userPermission?.rols || [];
      return engineerRoles.includes('tehnician');
    });
    return hasTechnicianAssigned;
  }

  // Dispatchers can edit files assigned to technicians
  if (userRoles.includes('dispatcher')) {
    const hasTechnicianAssigned = interventionFile?.helionEngineers?.some((engineer: any) => {
      const engineerRoles = engineer?.user?.userPermission?.rols || [];
      return engineerRoles.includes('tehnician');
    });
    return hasTechnicianAssigned;
  }

  return false;
};
